import type { PhaseEvent } from "@cmmd-forge/contracts";

import type { WsConnectionUiState, WsReconnectPhase } from "~/rpc/wsConnectionState";
import type { CloudRuntimeRouteBlockKind } from "~/routes/ensureActiveRouteCloudRuntime";

export type WorkspaceStartupOverlayPhase =
  | "workspace-vm"
  | "connecting"
  | "loading"
  | "worker-vm"
  | "runtime-blocked"
  | "unavailable";

/** Canonical non-connectable route states surfaced by the startup shell. */
type WorkspaceStartupRouteBlockKind = CloudRuntimeRouteBlockKind;

export const WORKSPACE_BOOTSTRAP_SLOW_MS = 45_000;
export const WORKSPACE_BOOTSTRAP_TIMEOUT_MS = 180_000;

export interface WorkspaceStartupOverlayModel {
  readonly title: string;
  readonly phase: WorkspaceStartupOverlayPhase;
}

interface WorkspaceStartupOverlayInput {
  readonly bootstrapComplete: boolean;
  readonly cloudMode: boolean;
  readonly wsUiState: WsConnectionUiState;
  readonly reconnectPhase: WsReconnectPhase;
  readonly bootstrapPendingMs?: number;
  readonly bootstrapSlowMs?: number;
  readonly bootstrapTimeoutMs?: number;
  readonly readinessPhase?: PhaseEvent | null;
  readonly blockedKind?: WorkspaceStartupRouteBlockKind | null;
}

const BLOCKED_RUNTIME_TITLES: Readonly<Record<WorkspaceStartupRouteBlockKind, string>> = {
  unprovisioned: "This project hasn't started yet",
  sleeping: "This project is asleep",
  paused: "This project is paused",
  error: "This project hit an error",
  destroyed: "This project needs to be recreated",
  draining: "Project update in progress",
  "update-required": "Project update required",
};

const CONNECTING_UI_STATES: ReadonlySet<WsConnectionUiState> = new Set([
  "connecting",
  "reconnecting",
  "offline",
]);

/**
 * Map a server-emitted PhaseEvent to user-facing overlay copy. Used when
 * the FINDING-CB1 workspace readiness channel is live (SSE pre-upgrade +
 * Effect RPC subscription post-upgrade). The mapping is the contract from
 * docs/superpowers/specs/2026-05-19-forge-cold-boot-progress-channel-
 * design.md Section 1 "Phase → UI copy".
 */
const readinessPhaseToOverlay = (event: PhaseEvent): WorkspaceStartupOverlayModel | null => {
  switch (event.phase) {
    case "idle":
    case "app-ready":
      // Workspace process readiness is not the same as client shell readiness.
      // Let the caller fall back to the pending-shell model until the shell
      // snapshot flips `bootstrapComplete`.
      return null;
    case "queued":
      return { phase: "workspace-vm", title: "Starting project" };
    case "listing":
      return { phase: "workspace-vm", title: "Locating your project" };
    case "rate-limited":
      return { phase: "workspace-vm", title: "Easing in" };
    case "creating":
      return { phase: "workspace-vm", title: "Provisioning project" };
    case "starting":
      return { phase: "workspace-vm", title: "Waking your project" };
    case "waiting-state":
      return { phase: "workspace-vm", title: "Booting" };
    case "image-pulling":
      return { phase: "workspace-vm", title: "Pulling environment" };
    case "supervisor-up":
      return { phase: "workspace-vm", title: "Almost ready" };
    case "reconnecting":
      // Steady-state reconnect blip — surface as `connecting` so the UI
      // can render it as a toast instead of the full-screen overlay.
      return { phase: "connecting", title: "Reconnecting" };
    case "error":
      return { phase: "unavailable", title: event.message };
  }
};

function resolveCloudPendingOverlay(input: {
  readonly pendingMs: number;
  readonly slowMs: number;
  readonly reconnectPhase: WsReconnectPhase;
}): WorkspaceStartupOverlayModel {
  if (input.reconnectPhase === "exhausted") {
    return {
      phase: "unavailable",
      title: "Project still starting",
    };
  }

  if (input.pendingMs >= input.slowMs) {
    return {
      phase: "workspace-vm",
      title: "Still starting",
    };
  }

  return {
    phase: "loading",
    title: "Connecting to project",
  };
}

function resolveLocalPendingOverlay(input: {
  readonly pendingMs: number;
  readonly slowMs: number;
  readonly reconnectPhase: WsReconnectPhase;
  readonly wsUiState: WsConnectionUiState;
}): WorkspaceStartupOverlayModel {
  if (input.reconnectPhase === "exhausted") {
    return {
      phase: "unavailable",
      title: "Project still starting",
    };
  }

  if (CONNECTING_UI_STATES.has(input.wsUiState)) {
    return {
      phase: "connecting",
      title: "Connecting",
    };
  }

  if (input.pendingMs >= input.slowMs) {
    return {
      phase: "worker-vm",
      title: "Still starting",
    };
  }

  return {
    phase: "loading",
    title: "",
  };
}

function resolvePendingStartupOverlay(
  input: WorkspaceStartupOverlayInput,
): WorkspaceStartupOverlayModel {
  const pendingMs = input.bootstrapPendingMs ?? 0;
  const slowMs = input.bootstrapSlowMs ?? WORKSPACE_BOOTSTRAP_SLOW_MS;
  const timeoutMs = input.bootstrapTimeoutMs ?? WORKSPACE_BOOTSTRAP_TIMEOUT_MS;

  if (pendingMs >= timeoutMs) {
    return {
      phase: "unavailable",
      title: "Project still starting",
    };
  }

  const pendingInput = {
    pendingMs,
    slowMs,
    reconnectPhase: input.reconnectPhase,
  };
  return input.cloudMode
    ? resolveCloudPendingOverlay(pendingInput)
    : resolveLocalPendingOverlay({ ...pendingInput, wsUiState: input.wsUiState });
}

function resolveReadinessOverlay(
  readinessPhase: PhaseEvent | null | undefined,
): WorkspaceStartupOverlayModel | null {
  if (readinessPhase === null || readinessPhase === undefined) {
    return null;
  }

  // `idle` / `app-ready` only say the runtime is reachable; the SPA still
  // needs the shell snapshot before rendering project/thread chrome.
  return readinessPhaseToOverlay(readinessPhase);
}

export function resolveWorkspaceStartupOverlayModel(
  input: WorkspaceStartupOverlayInput,
): WorkspaceStartupOverlayModel | null {
  if (input.bootstrapComplete) {
    return null;
  }

  if (input.blockedKind !== null && input.blockedKind !== undefined) {
    return {
      phase: "runtime-blocked",
      title: BLOCKED_RUNTIME_TITLES[input.blockedKind],
    };
  }

  const readinessOverlay = resolveReadinessOverlay(input.readinessPhase);
  return readinessOverlay ?? resolvePendingStartupOverlay(input);
}
